import { useEffect, useState } from 'react'
import { getSubmittalFilters } from '@/services/submittalService'

// Outside React, so it survives the unmount a tab switch causes.
const cache = new Map()

// The cache outlives a component, and so outlives a test.
export const clearFilterOptionsCache = () => cache.clear()

/**
 * Filter options for a listing, held across mounts.
 *
 * Every mount still refetches — a cached value is what renders while the
 * response is in flight, not a replacement for it. Without it, switching back to
 * a tab shows empty dropdowns for as long as the request takes, and a filter
 * with no options is not rendered at all, so the whole row would flicker.
 */
const useFilterOptions = (isEnabled, dataElementId) => {
  // Keyed, because a data element can change without a remount: an entry from
  // the previous one must not be read as this one's.
  const [entry, setEntry] = useState(null)
  const key = dataElementId || ''

  useEffect(() => {
    if (!isEnabled) return
    let current = true

    getSubmittalFilters(dataElementId)
      .then(data => {
        if (!data) return
        cache.set(key, data)
        if (current) setEntry({ key, data })
      })
      // A failed load leaves the dropdowns on whatever is cached rather than
      // clearing them; the table itself reports its own errors.
      .catch(() => {})

    return () => {
      current = false
    }
  }, [isEnabled, dataElementId, key])

  if (entry?.key === key) return entry.data
  // Read during render, so a warm key has its options on the first frame — an
  // effect could only supply them a frame later, which is the flash itself.
  return cache.get(key) || null
}

export default useFilterOptions
